/* eslint-disable max-lines -- no point in refactoring objectless files as they will be deleted  */
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Inject,
  Injector,
  Input,
  OnInit,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { isNil, matches } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { withHistory } from '@sama/rxjs-core';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { H3mlStore } from '../../../../../../../core/h3ml/state/h3ml-store';
import {
  getValuesForOutputContext,
  OutputInfo,
  outputInfoStoreToken,
  resetOutput,
} from '../../../../../../../core/h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../../../../core/h3ml/state/task-workspace-store';
import { ObjectlessFocusedShapeService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/objectless-focused-shape.service';
import { PushAndTrackService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/push-and-track.service';
import { FeatureFlagService } from '../../../../../../../core/shared/misc-services/feature-flag.service';
import { getShapesForEventTracking } from '../../../../../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import { DynamicTags } from '../../../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { isKeyLocation } from '../../../../../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import { isSupportedOutputForDefaultTags } from '../../../../../../../cross-team-shared/misc-services/tag-helper';
import { EventTrackingService } from '../../../../../../../shared/event-tracking/event-tracking.service';
import { getOutputHelper } from '../../../../../../../shared/output-helpers/get-output-helper';
import { AnswerOption } from '../../../../../../../team-apis/otterdam/answer-option';
import {
  AbstractOutput,
  AbstractOutputWithAnswerOptions,
} from '../../../../../../../team-apis/otterdam/outputs/output';
import { SensorFusionOutput } from '../../../../../../../team-apis/otterdam/outputs/sensor-fusion-output';
import { outputAnnotationInfoStoreToken } from '../../../../annotation-providers';
import { AbstractAnnotationStore } from '../../../../state/root/abstract-annotation-store';
import { TagService } from '../../../../utils/tag.service';

@Component({
  selector: 'h3-objectless-nested-output-panel',
  templateUrl: './objectless-nested-output-panel.component.html',
  styleUrls: ['./objectless-nested-output-panel.component.scss'],
  providers: [EventTrackingService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
/** @deprecated used for old projects without objects */
export class ObjectlessNestedOutputPanelComponent
  extends DirectiveSuperclass
  implements OnInit
{
  @Input() dynamic?: boolean;

  private readonly shapesStore = inject(outputAnnotationInfoStoreToken)(
    'shapes',
  );
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);

  nestedOutputInfosPlusMore!: Array<{
    label: string;
    required: boolean;
    isRequiredAndBlank$: Observable<boolean>;
    outputInfoStore: Store<OutputInfo>;
    hasKeyframe$?: Observable<boolean>;
    isFused: boolean;
    isDisplayed$: Observable<boolean>;
    keyframeTogglePointerEvents$: Observable<string>;
  }>;
  resetOutput = resetOutput;

  constructor(
    private h3mlStore: H3mlStore,
    @Inject(outputInfoStoreToken)
    private outputInfoStore: Store<OutputInfo<AbstractOutputWithAnswerOptions>>,
    private store: AbstractAnnotationStore,
    private tagService: TagService,
    private pushAndTrackService: PushAndTrackService,
    private eventTrackingService: EventTrackingService,
    private focusedShapeService: ObjectlessFocusedShapeService,
    private featureFlagService: FeatureFlagService,
    injector: Injector,
  ) {
    super(injector);
    this.eventTrackingService.addContext({
      parent_context: this.getParentOutputContext(),
    });
  }

  ngOnInit() {
    const fusedAnswerOptions = this.getFusedAnswerOptions();
    let answerOptions = this.outputInfoStore.state().output.answer_options;
    if (isDefined(this.dynamic)) {
      answerOptions = answerOptions.filter(matches({ dynamic: this.dynamic }));
    }
    this.nestedOutputInfosPlusMore = answerOptions.map((option) => {
      const outputInfoStore = this.h3mlStore('outputInfoByName')(option.name);
      this.setSubscriptionToDisplayed(outputInfoStore);
      return {
        label: this.labelText(option),
        required: outputInfoStore.state().output.required,
        isRequiredAndBlank$: outputInfoStore('answer').$.pipe(
          map((tag) => this.tagService.isTagRequiredAndBlank(tag, option.name)),
        ),
        hasKeyframe$: this.buildHasKeyframe$(option),
        isFused: !!fusedAnswerOptions.find((o) => o === option),
        outputInfoStore,
        isDisplayed$: outputInfoStore('isVisible').$,
        keyframeTogglePointerEvents$:
          this.buildKeyframeTogglePointerEvents$(option),
      };
    });
  }

  getFusedAnswerOptions() {
    let fusedAnswerOptions: AnswerOption[] = [];
    const firstFusedOutput = this.h3mlStore
      .state()
      .project.outputs.find(
        (o) => o.type === 'SensorFusionOutput',
      ) as SensorFusionOutput;
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (firstFusedOutput) {
      fusedAnswerOptions = firstFusedOutput.answer_options;
    }
    return fusedAnswerOptions;
  }

  removeKeyframe(outputInfoStore: Store<OutputInfo>) {
    const shapeStore = this.focusedShapeService.getFocusedShapeStore();
    if (!shapeStore) {
      throw new Error('no focused shape');
    }

    const outputInfo = outputInfoStore.state();
    const frameNumber = this.store.state().frameNumber;

    this.tagService.removeKeyframe(
      frameNumber,
      outputInfo,
      shapeStore.state().index,
    );

    this.pushAndTrackService.pushAndTrack({
      id: 'toggle_tag_keyframe',
      bucket: 'tag_selection',
      ...getValuesForOutputContext(outputInfo.output),
      parent_context: this.getParentOutputContext(),
      is_now_keyframe: false,
    });
  }

  shouldHideClearAnswerButton(outputInfo: Store<OutputInfo>) {
    return (
      getOutputHelper(outputInfo.state().output.type).isAnswerBlank(
        outputInfo.state().answer,
      ) || this.taskWorkspaceStore.state().readOnly
    );
  }

  private buildKeyframeTogglePointerEvents$(answerOption: AnswerOption) {
    const hasKeyframe$ = this.buildHasKeyframe$(answerOption);
    if (this.taskWorkspaceStore.state().readOnly || !hasKeyframe$) {
      return of('none');
    }

    return hasKeyframe$.pipe(
      map((hasKeyframe) => (hasKeyframe ? 'auto' : 'none')),
    );
  }

  private setSubscriptionToDisplayed(outputInfoStore: Store<OutputInfo>) {
    this.subscribeTo(
      this.isNestedOutputDisplayed$(outputInfoStore.state().output).pipe(
        distinctUntilChanged(),
        withHistory(1),
      ),
      ([shouldDisplay, wasDisplayed]) => {
        outputInfoStore.getRootStore().batch(() => {
          if (!shouldDisplay) {
            resetOutput(outputInfoStore);
          } else {
            const output = this.getValidOutputForDefaultAnswer(
              shouldDisplay,
              wasDisplayed,
              outputInfoStore.state().output,
            );
            if (output) {
              outputInfoStore('answer').set(output.answer_options[0].name);
            }
          }
          outputInfoStore('isVisible').set(shouldDisplay);
        });
      },
    );
  }

  private getValidOutputForDefaultAnswer(
    shouldDisplay: boolean,
    wasDisplayed: boolean | undefined,
    output: AbstractOutput,
  ) {
    return (
      this.featureFlagService.hasFlag('select_first_tag') &&
      shouldDisplay &&
      wasDisplayed === false &&
      isSupportedOutputForDefaultTags(output) &&
      output
    );
  }

  private isNestedOutputDisplayed$(
    output: AbstractOutput,
  ): Observable<boolean> {
    if (isNil(output.depends_on_answers_from_output_id)) {
      return of(true);
    }
    const parentOutput = this.tagService.getNestedOutput(
      output.depends_on_answers_from_output_id,
    )!;

    return this.tagService.isParentOptionSelected$(parentOutput, output);
  }

  private labelText(answerOption: AnswerOption) {
    if (answerOption.label) {
      return answerOption.label.trim().replace(/^#/, '');
    } else {
      return '';
    }
  }

  private buildHasKeyframe$(answerOption: AnswerOption) {
    if (!answerOption.dynamic) {
      return undefined;
    }

    return combineLatest([
      this.focusedShapeService
        .getFocusedShapeStore$()
        .pipe(
          switchMap((store) =>
            store
              ? store('dynamic_tags').$.pipe(filter(isDefined))
              : of({} as DynamicTags),
          ),
        ),
      this.store('frameNumber').$,
    ]).pipe(
      map(([dynamicTags, frameNumber]) =>
        // @ts-ignore -- fix me
        isKeyLocation(dynamicTags[answerOption.name], frameNumber),
      ),
    );
  }

  private getParentOutputContext() {
    const output = this.outputInfoStore.state().output;
    return {
      output_id: output.id,
      output_type: output.type,
      shapes: () => {
        const focusedShapeStore = this.getFocusedShapeStore();

        // @ts-ignore -- fix me
        return getShapesForEventTracking(this.shapesStore.state(), [
          focusedShapeStore.state().index,
        ]);
      },
    };
  }

  private getFocusedShapeStore() {
    const focusedShapeStore = this.focusedShapeService.getFocusedShapeStore();
    if (!focusedShapeStore) {
      throw new Error('events (should) only trigger on a focused shape');
    }
    return focusedShapeStore;
  }
}
